import { ThemeProvider as MuiThemeProvider, StylesProvider, jssPreset } from "@material-ui/core";
import { Rule, StyleSheet } from "jss";
import React from "react";

import { BentoTheme, ThemeAttribute, ThemeName, WeVideoTheme } from "../models/WeVideoTheme";
import { DarkTheme } from "./DarkTheme";
import { LightTheme } from "./LightTheme";

declare module "@material-ui/core/styles/createMuiTheme" {
	interface Theme {
		name: ThemeName;
		deprecatedTheme: WeVideoTheme; // TODO to be deprecated after full migration to bento
		bento: BentoTheme;
	}
	interface ThemeOptions {
		name: ThemeName;
		deprecatedTheme: WeVideoTheme; // TODO to be deprecated after full migration to bento
		bento: BentoTheme;
	}
}

export type Theme = typeof DarkTheme;

export type ComponentLibrary = "mui" | "aria";

const ComponentLibraryContext = React.createContext<ComponentLibrary>("mui");

export const useComponentLibrary = () => React.useContext(ComponentLibraryContext);

// Need to export these, otherwise TS will complain about inferred type of makeWeStyles
export type { ClassNameMap, Styles, WithStylesOptions } from "@material-ui/styles";
export { makeStyles as makeWeStyles, useTheme, useMediaQuery } from "@material-ui/core";

export { withTheme } from "@material-ui/core";

export { StylesProvider, jssPreset };

export const isCurrentTheme = (theme: ThemeName) =>
	window.document.documentElement.getAttribute(ThemeAttribute) === theme;

export const getCurrentTheme = () => {
	if (isCurrentTheme(ThemeName.DARK)) {
		return DarkTheme;
	}
	return LightTheme;
};

export const getCurrentThemeName = () => {
	if (isCurrentTheme(ThemeName.DARK)) {
		return ThemeName.DARK;
	}
	return ThemeName.LIGHT;
};

export const setCurrentTheme = (theme: ThemeName) =>
	window.document.documentElement.setAttribute(ThemeAttribute, theme);

export function ThemeProvider({
	theme: defaultTheme,
	children,
	isThemeImmutable = false,
	componentLibrary = "mui",
}: {
	theme?: Theme;
	children: React.ReactNode;
	isThemeImmutable?: boolean;
	componentLibrary?: ComponentLibrary;
}) {
	const currentTheme = getCurrentTheme();
	const [theme, setTheme] = React.useState<Theme>(defaultTheme ?? currentTheme);

	React.useEffect(() => {
		if (isThemeImmutable) return undefined;

		const targetNode = window.document.documentElement;

		const observer = new MutationObserver(() => {
			const currentTheme = getCurrentTheme();
			if (currentTheme) {
				setTheme(currentTheme);
			}
		});
		observer.observe(targetNode, {
			attributes: true,
			attributeFilter: [ThemeAttribute],
		});

		return () => observer.disconnect();
	}, [isThemeImmutable]);

	return (
		<ComponentLibraryContext.Provider value={componentLibrary}>
			<StylesProvider generateClassName={generateClassName}>
				<MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
			</StylesProvider>
		</ComponentLibraryContext.Provider>
	);
}

function generateClassName(rule: Rule, styleSheet?: StyleSheet<string>) {
	const { name, theme } = styleSheet?.options as any;

	// Is a global static MUI style?
	if (name && name.indexOf("Mui") === 0 && !styleSheet?.options.link) {
		// We can use a shorthand class name, we never use the keys to style the components.
		if (pseudoClasses.indexOf(rule.key) !== -1) {
			return `Mui-${rule.key}`;
		}

		const prefix = `${name}-${rule.key}`;

		if (theme && !theme[nested]) {
			return prefix;
		}

		return `${prefix}-${getNextCounterId()}`;
	}

	const suffix = `${rule.key}-${getNextCounterId()}`;
	return `makeStyles-${suffix}`;
}

let ruleCounter = 0;
function getNextCounterId() {
	ruleCounter += 1;

	return ruleCounter;
}

const hasSymbol = typeof Symbol === "function" && Symbol.for;
const nested = hasSymbol ? Symbol.for("mui.nested") : "__THEME_NESTED__";

const pseudoClasses = [
	"checked",
	"disabled",
	"error",
	"focused",
	"focusVisible",
	"required",
	"expanded",
	"selected",
];
